import { router } from 'expo-router';
import { Pressable, StyleSheet, View } from 'react-native';

import { IconChevronRight, InlineAlert, Skeleton, Text, XStack, YStack, tokens } from '@fhs/ui';
import { formatCentsToDollarString } from '@fhs/utils';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { isCreditCardExpired } from '@fhs-legacy/frontend/src/utils/payment';
import {
  CHECKOUT_PAYMENT_DETAILS_HEADING,
  CHECKOUT_PAYMENT_METHOD_BUTTON,
} from '@fhs-legacy/frontend/src/utils/test-ids';

import { useGiftCardCheckoutValidation } from '../../../hooks/use-gift-card-checkout-validation';
import { usePaymentMethodStore } from '../../../payment';
import { NewCardForm } from '../../../payment/components';
import { logPaymentMethodSelectionOpened } from '../../../utils/logging-utils';

import useAvailablePaymentMethods from './action-payment-method-selection/use-available-payment-methods';
import { getGiftCardDisplay, getPaymentMethodDisplay } from './payment-method-icon-map';
import PaymentMethodLoadingSkeleton, {
  paymentMethodRowStyles,
} from './payment-method-loading-skeleton';

export default function CheckoutPaymentDetails() {
  const { giftCardMissingAmount, selectedGiftCard, isBalanceLoading } =
    useGiftCardCheckoutValidation();
  const { isUserAccountsPending } = useAvailablePaymentMethods();
  const selectedPaymentMethod = usePaymentMethodStore(state => state.selectedPaymentMethod);
  const showAddPaymentMethod = usePaymentMethodStore(state => state.showAddPaymentMethod);
  const userAccountsLoadError = usePaymentMethodStore(state => state.userAccountsLoadError);
  const savedCardData = usePaymentMethodStore(state => state.savedCardData);
  const { logRBIEvent } = useCRMEventsContext();

  const paymentDisplay =
    selectedPaymentMethod === 'giftCard'
      ? getGiftCardDisplay(selectedGiftCard?.last4 ?? '')
      : getPaymentMethodDisplay(selectedPaymentMethod, savedCardData);

  const isSelectedCardExpired = Boolean(
    selectedPaymentMethod === 'savedCard' &&
      savedCardData &&
      isCreditCardExpired(savedCardData.expiryMonth, savedCardData.expiryYear)
  );

  const hasError = Boolean(giftCardMissingAmount) || isSelectedCardExpired;

  // Key to force re-render when balance updates - combines loading state and balance value
  // This ensures the component re-renders after userAccounts query completes
  const giftCardBalanceKey = `${isBalanceLoading}-${selectedGiftCard?.currentBalance ?? 0}`;

  const giftCardErrorMessage = giftCardMissingAmount
    ? `Your gift card balance is insufficient. Please add at least $${giftCardMissingAmount} to complete your order.`
    : '';

  const expiredCardErrorMessage = isSelectedCardExpired
    ? 'Card expired. Please update payment method to continue.'
    : '';

  const expiredCardExpiryLabel =
    isSelectedCardExpired && savedCardData
      ? `Exp. ${savedCardData.expiryMonth}/${savedCardData.expiryYear}`
      : '';

  const handleOpenPaymentSelection = () => {
    logPaymentMethodSelectionOpened(logRBIEvent);
    router.setParams({ action: 'payment' });
  };

  return (
    <>
      <Text.Heading type="four" style={styles.heading} testID={CHECKOUT_PAYMENT_DETAILS_HEADING}>
        Payment Details
      </Text.Heading>

      {isUserAccountsPending ? (
        <PaymentMethodLoadingSkeleton />
      ) : (
        <Pressable
          testID={CHECKOUT_PAYMENT_METHOD_BUTTON}
          style={paymentMethodRowStyles.anchor}
          focusable
          onPress={handleOpenPaymentSelection}
          onAccessibilityTap={handleOpenPaymentSelection}
          accessibilityRole="button"
          accessibilityLabel={`Payment method: ${paymentDisplay.label}`}
          accessibilityHint="Tap to change payment method"
        >
          {props => (
            <YStack
              style={[
                paymentMethodRowStyles.container,
                props.pressed && styles.pressedContainer,
                hasError && styles.errorContainer,
              ]}
            >
              <XStack style={paymentMethodRowStyles.cardContainer}>
                <View
                  style={[
                    paymentMethodRowStyles.iconCircle,
                    props.pressed && styles.iconCirclePressed,
                    hasError && styles.iconCircleError,
                  ]}
                >
                  {paymentDisplay.icon}
                </View>
                <YStack style={paymentMethodRowStyles.textContainer}>
                  <Text.Ui
                    weight="semibold"
                    size="lg"
                    style={[styles.text, hasError && styles.textError]}
                  >
                    {paymentDisplay.label}
                  </Text.Ui>
                  {expiredCardExpiryLabel && (
                    <Text.Ui size="md" style={[styles.text, styles.textError]}>
                      {expiredCardExpiryLabel}
                    </Text.Ui>
                  )}
                  {selectedPaymentMethod === 'giftCard' &&
                    (isBalanceLoading ? (
                      <Skeleton style={styles.balanceSkeleton} />
                    ) : (
                      <Text.Ui
                        key={giftCardBalanceKey}
                        size="md"
                        style={[styles.text, hasError && styles.textError]}
                      >
                        {`Balance: $${formatCentsToDollarString(selectedGiftCard?.currentBalance ?? 0)}`}
                      </Text.Ui>
                    ))}
                </YStack>
                <IconChevronRight
                  size={24}
                  color={hasError ? tokens.colors.$error : tokens.colors.$houseDark}
                />
              </XStack>
              {giftCardErrorMessage && (
                <InlineAlert type="error" message={giftCardErrorMessage} />
              )}
              {expiredCardErrorMessage && (
                <InlineAlert type="error" message={expiredCardErrorMessage} />
              )}
            </YStack>
          )}
        </Pressable>
      )}

      {/* After load or fetch failure: add-new-card when no saved method or error fallback */}
      {!isUserAccountsPending &&
        (userAccountsLoadError || showAddPaymentMethod || !selectedPaymentMethod) && (
          <NewCardForm />
        )}
    </>
  );
}

const styles = StyleSheet.create({
  heading: {
    marginTop: 20,
    marginBottom: 12,
    marginHorizontal: 28,
  },
  pressedContainer: {
    backgroundColor: tokens.colors.$houseLight,
  },
  errorContainer: {
    borderColor: tokens.colors.$error,
    borderWidth: 1,
  },
  text: {
    flex: 1,
  },
  iconCirclePressed: {
    backgroundColor: tokens.colors.$black10,
  },
  iconCircleError: {
    backgroundColor: tokens.colors.$errorBackground,
  },
  textError: {
    color: tokens.colors.$error,
  },
  balanceSkeleton: {
    height: 16,
    width: 100,
    borderRadius: 4,
  },
});
